import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import SuggestedKeywordsOnboarding from "components/account_management/feed_settings/SuggestedKeywordsOnboarding";
import KeywordInputTooltip from "components/account_management/signal_settings/keyword_input/keyword_input_tooltip/KeywordInputTooltip";
import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import Tooltip from "components/ui/tooltip/Tooltip";
import { useFeedPreviewCounts } from "lib/hooks/api/signals/useFeedPreviewCounts";
import UIcon from "lib/icons/untitled_ui/UIcon";
import {
  sysBackgroundInactive,
  sysStatusInProgress,
  sysStatusNegative,
  sysStatusPositive,
} from "lib/themes/colors";
import * as tracking from "lib/tracking";
import { fieldInteractionEvent } from "./tracking";

// Default preview scope when no `countries` are passed (onboarding has no country step).
const DEFAULT_COUNTRIES = ["United Kingdom", "Ireland"];

// Colours for the three-segment keyword-count progress indicator. Each segment
// fills as the keyword count crosses 5 and 10.
function getSegmentColors(count: number): [string, string, string] {
  let first: string = sysStatusPositive;
  if (count === 0) {
    first = sysBackgroundInactive;
  } else if (count < 5) {
    first = sysStatusNegative;
  } else if (count < 10) {
    first = sysStatusInProgress;
  }

  let second: string = sysStatusPositive;
  if (count < 5) {
    second = sysBackgroundInactive;
  } else if (count < 10) {
    second = sysStatusInProgress;
  }

  const third = count < 10 ? sysBackgroundInactive : sysStatusPositive;

  return [first, second, third];
}

type GeneratedFeedPreviewInputsProps = {
  keywords: string[];
  isLoading: boolean;
  onSubmit: (keywords: string[]) => void;
  countries?: string[];
};

function GeneratedFeedPreviewInputs({
  keywords,
  isLoading,
  onSubmit,
  countries = DEFAULT_COUNTRIES,
}: GeneratedFeedPreviewInputsProps) {
  const [dismissedKeywords, setDismissedKeywords] = React.useState<string[]>([]);
  const [manualKeyword, setManualKeyword] = React.useState<string>("");

  // onChange keeps `isValid` live as keywords change; the useFieldArray `required` rule
  // rejects an empty set and gates the Next button. Keep `required` — in RHF 7.62
  // `minLength` is skipped for an empty array, so `minLength: 1` alone wouldn't block it.
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<{ keywords: { keyword: string }[] }>({
    mode: "onChange",
    defaultValues: { keywords: [] },
  });

  const { fields, remove, append } = useFieldArray({
    control,
    name: "keywords",
    rules: { required: true, minLength: 1 },
  });

  // Drives the keyword-count progress indicator below.
  const keywordCount = watch("keywords").length;

  // Per-keyword match counts, layered onto the existing chips without restyling.
  const { keywordCounts, isLoadingCounts } = useFeedPreviewCounts({
    keywords: fields.map((field) => field.keyword),
    competitorIds: [],
    partnerIds: [],
    cpvCodes: [],
    countries,
  });

  const keywordsButtons = fields.map((field, index) => {
    const count = keywordCounts[field.keyword];
    const hasCount = count !== undefined;

    const button = (
      <KeywordChip
        type="button"
        onClick={() => {
          remove(index);
          setDismissedKeywords([...dismissedKeywords, field.keyword]);
          tracking.logEvent(tracking.EventNames.customKeywordAdded, {
            Action: "Deselected",
            "Page source": "Onboarding Page",
            "Keyword Type": "Custom keyword",
            "Keyword Name": field.keyword,
          });
          fieldInteractionEvent(field.keyword, "removeKeyword");
        }}
      >
        {field.keyword}
        {hasCount && ` (${count})`}
        {!hasCount && isLoadingCounts && (
          <span role="status" aria-label="Loading match count">
            <UIcon icon="spinner" size={12} />
          </span>
        )}
        <UIcon icon="xClose" size={12} />
      </KeywordChip>
    );

    // Tooltip only adds hover context — the chip styling is unchanged.
    return (
      <Tooltip
        key={field.id}
        content={hasCount ? <KeywordInputTooltip count={count} warning={false} /> : undefined}
      >
        {button}
      </Tooltip>
    );
  });

  const renderKeywordsProgressBar = (count: number) => {
    const [first, second, third] = getSegmentColors(count);
    return (
      <>
        <Flex gap={8}>
          <Segment barColor={first} />
          <Segment barColor={second} />
          <Segment barColor={third} />
        </Flex>
        <KeywordHelperText>
          {count === 0 &&
            "You need at least 1 keyword to get started. Try adding some suggested keywords, or typing your own."}
          {count >= 1 &&
            count < 5 &&
            "Start adding more keywords to see relevant results. Use our suggestions or type your own."}
          {count >= 5 &&
            count < 10 &&
            "Add a few more keywords, we recommend around 10 to 15 to get started."}
          {count >= 10 &&
            count < 15 &&
            `${count} keywords is a great start! Aim for 15 to supercharge your feed even further 🚀`}
          {count >= 15 && "Nice work, that will be a great start 🎉"}
        </KeywordHelperText>
      </>
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualKeyword(e.target.value);
  };

  return (
    <>
      <Header>
        <ProgressBarContainer>
          <ProgressBar />
        </ProgressBarContainer>
        <h1>What are you looking for?</h1>
        <p>
          Add keywords specific to your services and the contracts you're looking for, so we can
          find the most relevant notices for you.
        </p>
      </Header>
      <form
        onSubmit={handleSubmit((values) => {
          onSubmit(values.keywords.map((item) => item.keyword));
        })}
      >
        <Flex column style={{ paddingBottom: 16 }}>
          <SuggestedKeywordsOnboarding
            initialKeywords={keywords}
            onClick={(keyword: string) => {
              append({ keyword });
              tracking.logEvent(tracking.EventNames.customKeywordAdded, {
                "Page source": "Onboarding Page",
                "Context source": "Recommended",
                "Keyword Name": keyword,
              });
              fieldInteractionEvent(keyword, "recommendedKeyword");
            }}
            existingKeywords={watch("keywords").map((item) => item.keyword)}
            dismissedKeywords={dismissedKeywords}
          />
          <SectionLabel>Or add keywords manually</SectionLabel>
          <InputRow>
            <KeywordInputWrapper>
              <KeywordInputField
                value={manualKeyword}
                onChange={handleInputChange}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key !== "Enter") {
                    return;
                  }
                  e.preventDefault();
                  append({ keyword: e.currentTarget.value });
                  setManualKeyword("");
                  tracking.logEvent(tracking.EventNames.customKeywordAdded, {
                    "Page source": "Onboarding Page",
                    "Context source": "Freetyped",
                    "Keyword Name": e.currentTarget.value,
                  });
                  fieldInteractionEvent(e.currentTarget.value, "customKeyword");
                }}
                onBlur={(event) => {
                  fieldInteractionEvent(event.target.value, "customKeyword");
                }}
                placeholder="Type keyword and press enter"
              />
              <InputSuffix aria-hidden>
                <UIcon icon="plus" size={14} />
              </InputSuffix>
            </KeywordInputWrapper>
          </InputRow>

          <Divider />

          <SectionLabel>Your selected keywords</SectionLabel>
          {isLoading && (
            <Flex flexWrap="wrap" gap={8}>
              {Array.from({ length: 10 }).map((_, index) => (
                <SkeletonText key={index} variant="rectangular" width="114px" fontSize={32} />
              ))}
            </Flex>
          )}
          <Flex flexWrap="wrap" gap={8}>
            {keywordsButtons}{" "}
          </Flex>
        </Flex>

        {renderKeywordsProgressBar(keywordCount)}
        <FinishContainer>
          <Button buttonType="primary" disabled={!isValid} type="submit">
            Next
          </Button>
        </FinishContainer>
      </form>
    </>
  );
}

export default GeneratedFeedPreviewInputs;

const Header = styled.div(({ theme }) => ({
  marginBottom: 24,
  "& h1": {
    fontWeight: 600,
    fontSize: 24,
    lineHeight: "32px",
    color: theme.colors.sysTextDefault,
  },
  "& p": {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "22px",
    color: theme.colors.sysTextSecondary,
    paddingBottom: 0,
  },
}));

const ProgressBarContainer = styled.div({
  maxWidth: 200,
  marginBottom: 56,
});

const ProgressBar = styled.div(({ theme }) => ({
  height: 8,
  borderRadius: 100,
  backgroundColor: theme.colors.sysPrimaryHover,
}));

const SectionLabel = styled.label(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  lineHeight: "20px",
  color: theme.colors.sysTextDefault,
  marginBottom: 16,
}));

const InputRow = styled.div({
  marginBottom: 0,
});

const KeywordInputWrapper = styled.div({
  position: "relative",
  width: 320,
});

const KeywordInputField = styled.input(({ theme }) => ({
  width: "100%",
  height: 32,
  padding: "4px 32px 4px 11px",
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  fontSize: 14,
  lineHeight: "22px",
  color: theme.colors.sysTextDefault,
  outline: "none",
  "&:focus": {
    borderColor: theme.colors.sysPrimaryDefault,
  },
  "&::placeholder": {
    color: theme.colors.sysTextPlaceholder,
  },
}));

const InputSuffix = styled.span(({ theme }) => ({
  position: "absolute",
  top: "50%",
  right: 11,
  transform: "translateY(-50%)",
  display: "inline-flex",
  color: theme.colors.sysTextPlaceholder,
  pointerEvents: "none",
}));

const Divider = styled.hr(({ theme }) => ({
  width: "100%",
  border: "none",
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  margin: "24px 0",
}));

const KeywordChip = styled.button(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  backgroundColor: theme.colors.sysInfoSubtle,
  border: `1px solid ${theme.colors.sysInfoSubtleAlt}`,
  borderRadius: 4,
  padding: "0 8px",
  height: 32,
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "20px",
  color: theme.colors.sysTextDefault,
  cursor: "pointer",
  "&:hover, &:focus, &:active": {
    borderColor: theme.colors.sysTextDefault,
    color: theme.colors.sysTextDefault,
  },
}));

const Segment = styled.div<{ barColor: string }>(({ barColor }) => ({
  flex: 1,
  height: 8,
  borderRadius: 100,
  backgroundColor: barColor,
}));

const KeywordHelperText = styled.p(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "20px",
  marginBottom: 24,
  color: theme.colors.sysTextSecondary,
}));

const FinishContainer = styled.div({
  position: "sticky",
  bottom: 0,
  background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)",
  paddingTop: 24,
  paddingBottom: 24,
});
